/**
 * Atelier Theme -- Global JavaScript
 * Luxury Fashion Shopify OS 2.0 Theme
 *
 * Table of Contents:
 * 1.  Namespace & Utilities
 * 2.  Cart API Wrapper
 * 3.  Header Scroll Behavior
 * 4.  Menu Drawer
 * 5.  Cart Drawer
 * 6.  Search Drawer
 * 7.  Product Page
 * 8.  Collection Page
 * 9.  Scroll Animations
 * 10. Lazy Loading
 * 11. Custom Events
 * 12. Shopify Section Rendering API
 * 13. Accessibility
 */

/* ============================================
   1. ATELIER NAMESPACE & UTILITIES
   ============================================ */

window.Atelier = window.Atelier || {};

/**
 * Debounce: delays function execution until after a pause in calls.
 * @param {Function} fn - The function to debounce.
 * @param {number} delay - Milliseconds to wait.
 * @returns {Function}
 */
Atelier.debounce = function (fn, delay) {
  if (delay === undefined) delay = 250;
  var timer;
  return function () {
    var context = this;
    var args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fn.apply(context, args);
    }, delay);
  };
};

/**
 * Throttle: ensures function executes at most once per interval.
 * @param {Function} fn - The function to throttle.
 * @param {number} limit - Milliseconds between allowed calls.
 * @returns {Function}
 */
Atelier.throttle = function (fn, limit) {
  if (limit === undefined) limit = 100;
  var waiting = false;
  return function () {
    if (!waiting) {
      fn.apply(this, arguments);
      waiting = true;
      setTimeout(function () {
        waiting = false;
      }, limit);
    }
  };
};

/**
 * Build a fetch config object for Shopify AJAX calls.
 * @param {string} type - Response type, defaults to 'json'.
 * @returns {Object}
 */
Atelier.fetchConfig = function (type) {
  if (type === undefined) type = 'json';
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/' + type
    }
  };
};

/**
 * Format cents into a currency string using Shopify money_format.
 * Supports placeholders: {{amount}}, {{amount_no_decimals}},
 * {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}.
 * @param {number|string} cents - Price in cents.
 * @param {string} [format] - Money format string.
 * @returns {string}
 */
Atelier.formatMoney = function (cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  cents = parseInt(cents, 10) || 0;
  format = format || (window.Shopify && window.Shopify.money_format) || '${{amount}}';

  var dollars = (cents / 100).toFixed(2);
  var dollarsWhole = Math.floor(cents / 100);

  function addCommas(numStr) {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function addDots(numStr) {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  var result = format;
  result = result.replace('{{amount}}', addCommas(dollars));
  result = result.replace('{{amount_no_decimals}}', addCommas(String(dollarsWhole)));
  result = result.replace(
    '{{amount_with_comma_separator}}',
    addDots(String(dollarsWhole)) + ',' + dollars.split('.')[1]
  );
  result = result.replace(
    '{{amount_no_decimals_with_comma_separator}}',
    addDots(String(dollarsWhole))
  );

  return result;
};

/**
 * Trap keyboard focus within a container element (for drawers/modals).
 * @param {HTMLElement} container - The element to trap focus within.
 * @param {string} [namespace] - Optional namespace for event listener removal.
 */
Atelier.trapFocus = function (container, namespace) {
  var focusableSelector =
    'a[href], area[href], input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), ' +
    'button:not([disabled]), iframe, object, embed, [tabindex="0"], ' +
    '[contenteditable], audio[controls], video[controls], summary';

  var focusableElements = container.querySelectorAll(focusableSelector);
  var firstFocusable = focusableElements[0];
  var lastFocusable = focusableElements[focusableElements.length - 1];

  if (!firstFocusable) return;

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  var eventName = 'keydown' + (namespace ? '.' + namespace : '');
  container.setAttribute('data-trap-focus', namespace || 'default');
  container.addEventListener('keydown', handleKeydown);
  container._atelierTrapHandler = handleKeydown;

  firstFocusable.focus();
};

/**
 * Remove focus trap from a container.
 * @param {string} [namespace] - The namespace used when trapping.
 */
Atelier.removeTrapFocus = function (namespace) {
  var selector = '[data-trap-focus="' + (namespace || 'default') + '"]';
  var container = document.querySelector(selector);
  if (container && container._atelierTrapHandler) {
    container.removeEventListener('keydown', container._atelierTrapHandler);
    delete container._atelierTrapHandler;
    container.removeAttribute('data-trap-focus');
  }
};

/**
 * Pause all playing media elements on the page (videos, iframes).
 */
Atelier.pauseAllMedia = function () {
  document.querySelectorAll('video').forEach(function (video) {
    video.pause();
  });
  document.querySelectorAll('iframe').forEach(function (iframe) {
    try {
      var src = iframe.getAttribute('src');
      if (src && src.indexOf('youtube') !== -1) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
          '*'
        );
      } else if (src && src.indexOf('vimeo') !== -1) {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    } catch (e) {
      // Cross-origin iframe, ignore
    }
  });
};

/**
 * Lock body scroll (for drawers/modals).
 */
Atelier.lockScroll = function () {
  document.body.style.overflow = 'hidden';
  document.body.style.touchAction = 'none';
  document.body.setAttribute('data-scroll-locked', '');
};

/**
 * Unlock body scroll.
 */
Atelier.unlockScroll = function () {
  document.body.style.overflow = '';
  document.body.style.touchAction = '';
  document.body.removeAttribute('data-scroll-locked');
};

/* ============================================
   2. CART API WRAPPER
   ============================================ */

Atelier.cart = {
  /**
   * Get the current cart state.
   * @returns {Promise<Object>}
   */
  get: async function () {
    var response = await fetch('/cart.js');
    return response.json();
  },

  /**
   * Add one or more items to the cart.
   * @param {Object|Array} items - Item(s) with id, quantity, and optional properties.
   * @returns {Promise<Object>}
   */
  add: async function (items) {
    var payload = { items: Array.isArray(items) ? items : [items] };
    var response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      var errorData = await response.json().catch(function () { return {}; });
      var error = new Error(errorData.description || 'Add to cart failed');
      error.status = response.status;
      throw error;
    }
    var data = await response.json();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data;
  },

  /**
   * Update cart quantities by variant ID.
   * @param {Object} updates - Map of variant ID to quantity.
   * @returns {Promise<Object>}
   */
  update: async function (updates) {
    var response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
    var data = await response.json();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data;
  },

  /**
   * Change an individual line item quantity by key.
   * @param {string} key - The line item key.
   * @param {number} quantity - New quantity (0 to remove).
   * @returns {Promise<Object>}
   */
  change: async function (key, quantity) {
    var response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
    var data = await response.json();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data;
  },

  /**
   * Clear all items from the cart.
   * @returns {Promise<Object>}
   */
  clear: async function () {
    var response = await fetch('/cart/clear.js', { method: 'POST' });
    var data = await response.json();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data;
  }
};

/* ============================================
   3. HEADER SCROLL BEHAVIOR
   ============================================ */

(function () {
  var header = document.querySelector('.header');
  if (!header) return;

  var lastScrollY = 0;
  var ticking = false;
  var threshold = 50;
  var hideThreshold = 200;

  function onScroll() {
    var scrollY = window.scrollY;

    // Toggle condensed/scrolled state
    /* header.classList.toggle('is-scrolled', scrollY > threshold); // disabled for new header */

    // Auto-hide header on scroll down, reveal on scroll up
    if (scrollY > lastScrollY && scrollY > hideThreshold) {
      /* header.classList.add('is-hidden'); // disabled for new header */
    } else {
      /* header.classList.remove('is-hidden'); // disabled for new header */
    }

    lastScrollY = scrollY;
    ticking = false;
  }

  window.addEventListener(
    'scroll',
    function () {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    },
    { passive: true }
  );
})();

/* ============================================
   4. MENU DRAWER
   ============================================ */

(function () {
  var drawer = document.querySelector('.menu-drawer');
  var overlay = document.querySelector('.overlay');
  var openBtns = document.querySelectorAll('[data-menu-toggle]');
  var closeBtns = drawer ? drawer.querySelectorAll('[data-menu-close]') : [];
  var backBtns = drawer ? drawer.querySelectorAll('[data-menu-back]') : [];

  if (!drawer) return;

  var previousFocus = null;

  function open() {
    previousFocus = document.activeElement;
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-active');
    Atelier.lockScroll();
    Atelier.trapFocus(drawer, 'menu');
    document.dispatchEvent(new CustomEvent('menu:opened'));
  }

  function close() {
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-active');
    Atelier.unlockScroll();
    Atelier.removeTrapFocus('menu');

    // Reset sub-menus to top level
    drawer.querySelectorAll('.menu-drawer__panel').forEach(function (panel) {
      panel.classList.remove('is-active');
    });
    var mainPanel = drawer.querySelector('.menu-drawer__panel--main');
    if (mainPanel) mainPanel.classList.add('is-active');

    if (previousFocus) previousFocus.focus();
    document.dispatchEvent(new CustomEvent('menu:closed'));
  }

  openBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
  });

  closeBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      close();
    });
  });

  // Overlay click closes drawer
  if (overlay) {
    overlay.addEventListener('click', close);
  }

  // Multi-level navigation: open sub-panels
  drawer.querySelectorAll('[data-menu-child]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      var targetId = this.getAttribute('data-menu-child');
      var target = drawer.querySelector('#' + targetId);
      if (target) {
        drawer.querySelectorAll('.menu-drawer__panel').forEach(function (p) {
          p.classList.remove('is-active');
        });
        target.classList.add('is-active');
      }
    });
  });

  // Back buttons: return to parent panel
  backBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var parentId = this.getAttribute('data-menu-back');
      var parent = drawer.querySelector('#' + parentId);
      if (parent) {
        drawer.querySelectorAll('.menu-drawer__panel').forEach(function (p) {
          p.classList.remove('is-active');
        });
        parent.classList.add('is-active');
      }
    });
  });
})();

/* ============================================
   5. CART DRAWER
   ============================================ */

(function () {
  var cartDrawer = document.querySelector('.cart-drawer');
  var overlay = document.querySelector('.overlay');
  var openBtns = document.querySelectorAll('[data-cart-toggle]');
  var closeBtns = cartDrawer ? cartDrawer.querySelectorAll('[data-cart-close]') : [];

  if (!cartDrawer) return;

  var previousFocus = null;
  var cartBody = cartDrawer.querySelector('.cart-drawer__body');
  var cartCount = document.querySelectorAll('[data-cart-count]');
  var shippingBar = cartDrawer.querySelector('[data-shipping-bar]');

  function open() {
    previousFocus = document.activeElement;
    cartDrawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-active');
    Atelier.lockScroll();
    Atelier.trapFocus(cartDrawer, 'cart-drawer');
    document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
  }

  function close() {
    cartDrawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-active');
    Atelier.unlockScroll();
    Atelier.removeTrapFocus('cart-drawer');
    if (previousFocus) previousFocus.focus();
    document.dispatchEvent(new CustomEvent('cart-drawer:closed'));
  }

  openBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
  });

  closeBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      close();
    });
  });

  // Close on overlay click only if cart drawer is open
  if (overlay) {
    overlay.addEventListener('click', function () {
      if (cartDrawer.classList.contains('is-open')) {
        close();
      }
    });
  }

  /**
   * Update the shipping progress bar based on cart total.
   * @param {number} totalCents - Current cart total in cents.
   */
  function updateShippingBar(totalCents) {
    if (!shippingBar) return;
    var threshold = parseInt(shippingBar.getAttribute('data-shipping-threshold'), 10) || 0;
    if (threshold <= 0) return;

    var progress = Math.min((totalCents / threshold) * 100, 100);
    var fill = shippingBar.querySelector('.shipping-bar__fill');
    var message = shippingBar.querySelector('.shipping-bar__message');

    if (fill) fill.style.width = progress + '%';

    if (message) {
      if (totalCents >= threshold) {
        message.textContent = shippingBar.getAttribute('data-shipping-reached') || 'Free shipping!';
      } else {
        var remaining = Atelier.formatMoney(threshold - totalCents);
        var template = shippingBar.getAttribute('data-shipping-away') || 'You are {{amount}} away from free shipping';
        message.textContent = template.replace('{{amount}}', remaining);
      }
    }
  }

  /**
   * Update cart count badges throughout the page.
   * @param {number} count - The item count.
   */
  function updateCartCount(count) {
    cartCount.forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
    });
  }

  /**
   * Re-render the cart drawer contents via AJAX using the Section Rendering API.
   */
  async function refreshCartDrawer() {
    try {
      var sectionId = cartDrawer.getAttribute('data-section-id') || 'cart-drawer';
      var response = await fetch('/?sections=' + sectionId);
      var sections = await response.json();

      if (sections[sectionId]) {
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = sections[sectionId];
        var newBody = tempDiv.querySelector('.cart-drawer__body');
        if (newBody && cartBody) {
          cartBody.innerHTML = newBody.innerHTML;
          bindCartItemHandlers();
        }
      }

      // Update count and shipping bar from cart state
      var cart = await Atelier.cart.get();
      updateCartCount(cart.item_count);
      updateShippingBar(cart.total_price);
    } catch (e) {
      console.error('Atelier: Failed to refresh cart drawer', e);
    }
  }

  /**
   * Bind event handlers for quantity buttons and remove buttons inside cart drawer.
   */
  function bindCartItemHandlers() {
    // Quantity change buttons
    cartDrawer.querySelectorAll('[data-quantity-change]').forEach(function (btn) {
      btn.addEventListener('click', async function (e) {
        e.preventDefault();
        var key = this.getAttribute('data-line-key');
        var direction = this.getAttribute('data-quantity-change');
        var input = this.closest('.cart-item__quantity').querySelector('input');
        var currentQty = parseInt(input.value, 10) || 0;
        var newQty = direction === 'plus' ? currentQty + 1 : Math.max(0, currentQty - 1);

        this.disabled = true;
        try {
          await Atelier.cart.change(key, newQty);
        } catch (err) {
          console.error('Atelier: Quantity change failed', err);
        }
        this.disabled = false;
      });
    });

    // Remove item buttons
    cartDrawer.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
      btn.addEventListener('click', async function (e) {
        e.preventDefault();
        var key = this.getAttribute('data-line-key');
        try {
          await Atelier.cart.change(key, 0);
        } catch (err) {
          console.error('Atelier: Remove item failed', err);
        }
      });
    });
  }

  // Initial binding
  bindCartItemHandlers();

  // Listen for cart updates to refresh drawer
  document.addEventListener('cart:updated', function () {
    refreshCartDrawer();
  });

  // Expose open/close for external use
  Atelier.cartDrawer = { open: open, close: close };
})();

/* ============================================
   6. SEARCH DRAWER
   ============================================ */

(function () {
  var searchDrawer = document.querySelector('.search-drawer');
  var overlay = document.querySelector('.overlay');
  var openBtns = document.querySelectorAll('[data-search-toggle]');
  var closeBtns = searchDrawer ? searchDrawer.querySelectorAll('[data-search-close]') : [];

  if (!searchDrawer) return;

  var previousFocus = null;
  var searchInput = searchDrawer.querySelector('[data-search-input]');
  var resultsContainer = searchDrawer.querySelector('[data-search-results]');

  function open() {
    previousFocus = document.activeElement;
    searchDrawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-active');
    Atelier.lockScroll();
    Atelier.trapFocus(searchDrawer, 'search');
    if (searchInput) {
      setTimeout(function () { searchInput.focus(); }, 100);
    }
    document.dispatchEvent(new CustomEvent('search:opened'));
  }

  function close() {
    searchDrawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-active');
    Atelier.unlockScroll();
    Atelier.removeTrapFocus('search');
    if (previousFocus) previousFocus.focus();
    document.dispatchEvent(new CustomEvent('search:closed'));
  }

  openBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
  });

  closeBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      close();
    });
  });

  /**
   * Fetch predictive search results from Shopify.
   * @param {string} query - The search term.
   */
  async function predictiveSearch(query) {
    if (!resultsContainer) return;

    if (query.length < 2) {
      resultsContainer.innerHTML = '';
      resultsContainer.classList.remove('is-active');
      return;
    }

    try {
      var url =
        '/search/suggest.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,collection,article,page' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';

      var response = await fetch(url);
      var data = await response.json();
      var resources = data.resources && data.resources.results;

      if (!resources) {
        resultsContainer.innerHTML = '';
        resultsContainer.classList.remove('is-active');
        return;
      }

      var html = '';

      // Products
      if (resources.products && resources.products.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading">Products</h4>';
        resources.products.forEach(function (product) {
          var image = product.image || '';
          var price = Atelier.formatMoney(product.price);
          html += '<a href="' + product.url + '" class="search-results__item">';
          if (image) {
            html += '<img src="' + image + '" alt="' + product.title + '" width="60" height="75" loading="lazy">';
          }
          html += '<div class="search-results__info">';
          html += '<span class="search-results__title">' + product.title + '</span>';
          html += '<span class="search-results__price">' + price + '</span>';
          html += '</div></a>';
        });
        html += '</div>';
      }

      // Collections
      if (resources.collections && resources.collections.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading">Collections</h4>';
        resources.collections.forEach(function (collection) {
          html += '<a href="' + collection.url + '" class="search-results__item">';
          html += '<span class="search-results__title">' + collection.title + '</span>';
          html += '</a>';
        });
        html += '</div>';
      }

      // Articles
      if (resources.articles && resources.articles.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading">Journal</h4>';
        resources.articles.forEach(function (article) {
          html += '<a href="' + article.url + '" class="search-results__item">';
          html += '<span class="search-results__title">' + article.title + '</span>';
          html += '</a>';
        });
        html += '</div>';
      }

      // Pages
      if (resources.pages && resources.pages.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading">Pages</h4>';
        resources.pages.forEach(function (page) {
          html += '<a href="' + page.url + '" class="search-results__item">';
          html += '<span class="search-results__title">' + page.title + '</span>';
          html += '</a>';
        });
        html += '</div>';
      }

      // View all link
      html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="search-results__view-all btn btn--underline">';
      html += 'View all results</a>';

      resultsContainer.innerHTML = html;
      resultsContainer.classList.add('is-active');
    } catch (e) {
      console.error('Atelier: Predictive search failed', e);
    }
  }

  // Debounced input handler
  if (searchInput) {
    searchInput.addEventListener(
      'input',
      Atelier.debounce(function () {
        predictiveSearch(this.value.trim());
      }, 300)
    );
  }

  // Expose open/close for external use
  Atelier.searchDrawer = { open: open, close: close };
})();

/* ============================================
   7. PRODUCT PAGE
   ============================================ */

(function () {
  var productForm = document.querySelector('[data-product-form]');
  if (!productForm) return;

  var variantSelectors = productForm.querySelectorAll('[data-variant-option]');
  var priceEl = document.querySelector('[data-product-price]');
  var comparePriceEl = document.querySelector('[data-product-compare-price]');
  var addToCartBtn = productForm.querySelector('[data-add-to-cart]');
  var addToCartText = productForm.querySelector('[data-add-to-cart-text]');
  var variantIdInput = productForm.querySelector('input[name="id"]');
  var productJson = document.querySelector('[data-product-json]');
  var product = null;

  if (productJson) {
    try {
      product = JSON.parse(productJson.textContent);
    } catch (e) {
      console.error('Atelier: Invalid product JSON', e);
      return;
    }
  }

  if (!product) return;

  /**
   * Get currently selected option values.
   * @returns {Array<string>}
   */
  function getSelectedOptions() {
    var options = [];
    variantSelectors.forEach(function (selector) {
      if (selector.tagName === 'SELECT') {
        options.push(selector.value);
      } else if (selector.tagName === 'FIELDSET') {
        var checked = selector.querySelector('input:checked');
        if (checked) options.push(checked.value);
      }
    });
    return options;
  }

  /**
   * Find the variant matching the currently selected options.
   * @returns {Object|null}
   */
  function getSelectedVariant() {
    var selected = getSelectedOptions();
    return product.variants.find(function (variant) {
      return variant.options.every(function (opt, index) {
        return opt === selected[index];
      });
    }) || null;
  }

  /**
   * Update the product page UI to reflect the selected variant.
   * @param {Object|null} variant
   */
  function updateProductUI(variant) {
    if (!variant) {
      // Unavailable combination
      if (addToCartBtn) addToCartBtn.disabled = true;
      if (addToCartText) addToCartText.textContent = 'Unavailable';
      return;
    }

    // Update hidden variant ID
    if (variantIdInput) variantIdInput.value = variant.id;

    // Update price
    if (priceEl) priceEl.textContent = Atelier.formatMoney(variant.price);

    // Update compare-at price
    if (comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = Atelier.formatMoney(variant.compare_at_price);
        comparePriceEl.classList.remove('hidden');
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.classList.add('hidden');
      }
    }

    // Update availability
    if (addToCartBtn) addToCartBtn.disabled = !variant.available;
    if (addToCartText) {
      addToCartText.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }

    // Update URL without page reload
    if (variant.id) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Update product images: scroll to variant image if featured_image exists
    if (variant.featured_image) {
      var imageEl = document.querySelector(
        '[data-media-id="' + variant.featured_image.id + '"]'
      );
      if (imageEl) {
        imageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    // Dispatch event for external listeners
    document.dispatchEvent(
      new CustomEvent('variant:changed', { detail: { variant: variant } })
    );
  }

  // Listen for option changes
  variantSelectors.forEach(function (selector) {
    var eventType = selector.tagName === 'SELECT' ? 'change' : 'change';
    selector.addEventListener(eventType, function () {
      var variant = getSelectedVariant();
      updateProductUI(variant);
    });
  });

  // Add to cart form submission
  productForm.addEventListener('submit', async function (e) {
    e.preventDefault();

    var variant = getSelectedVariant();
    if (!variant || !variant.available) return;

    var quantityInput = productForm.querySelector('[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    if (addToCartBtn) addToCartBtn.disabled = true;
    if (addToCartText) addToCartText.textContent = 'Adding...';

    try {
      await Atelier.cart.add({ id: variant.id, quantity: quantity });

      if (addToCartText) addToCartText.textContent = 'Added';

      // Open cart drawer after add
      if (Atelier.cartDrawer) {
        Atelier.cartDrawer.open();
      }

      setTimeout(function () {
        if (addToCartBtn) addToCartBtn.disabled = false;
        if (addToCartText) addToCartText.textContent = 'Add to bag';
      }, 1500);
    } catch (err) {
      console.error('Atelier: Add to cart failed', err);
      if (addToCartText) addToCartText.textContent = err.message || 'Error';
      setTimeout(function () {
        if (addToCartBtn) addToCartBtn.disabled = false;
        if (addToCartText) addToCartText.textContent = 'Add to bag';
      }, 2000);
    }
  });

  // Sticky add-to-cart bar: show when main CTA scrolls out of view
  var stickyBar = document.querySelector('[data-sticky-add-to-cart]');
  if (stickyBar && addToCartBtn) {
    var stickyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    stickyObserver.observe(addToCartBtn);
  }

  // Accordion/tab toggles for product details
  document.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      var content = this.nextElementSibling;
      var isOpen = this.getAttribute('aria-expanded') === 'true';

      this.setAttribute('aria-expanded', !isOpen);
      if (content) {
        content.style.maxHeight = isOpen ? '0' : content.scrollHeight + 'px';
        content.setAttribute('aria-hidden', isOpen);
      }
    });
  });
})();

/* ============================================
   8. COLLECTION PAGE
   ============================================ */

(function () {
  var collectionGrid = document.querySelector('[data-collection-grid]');
  if (!collectionGrid) return;

  // Column count toggle
  document.querySelectorAll('[data-columns-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var columns = this.getAttribute('data-columns-toggle');

      // Remove existing grid column classes
      collectionGrid.className = collectionGrid.className.replace(/grid--\d/g, '').trim();
      collectionGrid.classList.add('grid--' + columns);

      // Update active state on toggle buttons
      document.querySelectorAll('[data-columns-toggle]').forEach(function (b) {
        b.classList.remove('is-active');
      });
      this.classList.add('is-active');
    });
  });

  // Filter form AJAX submission
  var filterForm = document.querySelector('[data-filter-form]');
  if (filterForm) {
    filterForm.addEventListener('submit', function (e) {
      e.preventDefault();
      applyFilters();
    });

    filterForm.addEventListener('change', Atelier.debounce(function () {
      applyFilters();
    }, 500));
  }

  async function applyFilters() {
    if (!filterForm) return;

    var formData = new FormData(filterForm);
    var params = new URLSearchParams(formData);
    var sectionId = collectionGrid.getAttribute('data-section-id');
    var url = window.location.pathname + '?' + params.toString();

    collectionGrid.classList.add('is-loading');

    try {
      var fetchUrl = url + (sectionId ? '&sections=' + sectionId : '');
      var response = await fetch(fetchUrl);

      if (sectionId) {
        var sections = await response.json();
        if (sections[sectionId]) {
          var tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections[sectionId];
          var newGrid = tempDiv.querySelector('[data-collection-grid]');
          if (newGrid) {
            collectionGrid.innerHTML = newGrid.innerHTML;
          }
        }
      } else {
        var html = await response.text();
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        var newGrid = tempDiv.querySelector('[data-collection-grid]');
        if (newGrid) {
          collectionGrid.innerHTML = newGrid.innerHTML;
        }
      }

      // Update URL without reload
      window.history.pushState({}, '', url);
    } catch (e) {
      console.error('Atelier: Filter failed', e);
    }

    collectionGrid.classList.remove('is-loading');
  }

  // Load More / Infinite Scroll pagination
  var loadMoreBtn = document.querySelector('[data-load-more]');
  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', async function (e) {
      e.preventDefault();
      var nextUrl = this.getAttribute('data-next-url');
      if (!nextUrl) return;

      this.disabled = true;
      this.textContent = 'Loading...';

      try {
        var response = await fetch(nextUrl);
        var html = await response.text();
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Append new product cards
        var newItems = tempDiv.querySelectorAll('[data-collection-grid] > *');
        newItems.forEach(function (item) {
          collectionGrid.appendChild(item);
        });

        // Update load more button with next page URL
        var newLoadMore = tempDiv.querySelector('[data-load-more]');
        if (newLoadMore) {
          loadMoreBtn.setAttribute('data-next-url', newLoadMore.getAttribute('data-next-url'));
          loadMoreBtn.textContent = 'Load more';
          loadMoreBtn.disabled = false;
        } else {
          loadMoreBtn.remove();
        }

        // Re-observe new items for animations
        if (window.Atelier._animationObserver) {
          collectionGrid.querySelectorAll('[data-animate]:not(.is-visible)').forEach(function (el) {
            window.Atelier._animationObserver.observe(el);
          });
        }
      } catch (e) {
        console.error('Atelier: Load more failed', e);
        loadMoreBtn.textContent = 'Load more';
        loadMoreBtn.disabled = false;
      }
    });
  }
})();

/* ============================================
   9. SCROLL ANIMATIONS
   ============================================ */

(function () {
  if (!('IntersectionObserver' in window)) return;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
  );

  document.querySelectorAll('[data-animate]').forEach(function (el) {
    observer.observe(el);
  });

  // Store reference for re-initialization (e.g., after AJAX load)
  window.Atelier._animationObserver = observer;
})();

/* ============================================
   10. LAZY LOADING
   ============================================ */

(function () {
  // Native lazy loading is supported in modern browsers via loading="lazy".
  // This provides a fallback for older browsers using IntersectionObserver.
  if ('loading' in HTMLImageElement.prototype) return;

  if (!('IntersectionObserver' in window)) return;

  var lazyObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          img.classList.add('is-loaded');
          lazyObserver.unobserve(img);
        }
      });
    },
    { rootMargin: '200px 0px' }
  );

  document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
    lazyObserver.observe(img);
  });
})();

/* ============================================
   11. CUSTOM EVENTS REGISTRY
   ============================================ */

/**
 * Custom events used throughout the theme:
 *
 * cart:updated          - Fired after any cart AJAX operation (add, change, update, clear).
 * variant:changed       - Fired when a product variant selection changes.
 *                         Detail: { variant: Object }
 * menu:opened           - Fired when the menu drawer opens.
 * menu:closed           - Fired when the menu drawer closes.
 * cart-drawer:opened    - Fired when the cart drawer opens.
 * cart-drawer:closed    - Fired when the cart drawer closes.
 * search:opened         - Fired when the search drawer opens.
 * search:closed         - Fired when the search drawer closes.
 * atelier:section-loaded - Fired after a section is re-initialized by the editor.
 *                          Detail: { sectionId: string }
 */

/* ============================================
   12. SHOPIFY SECTION RENDERING API
   ============================================ */

(function () {
  /**
   * Re-initialize section-specific JavaScript when Shopify theme editor
   * loads, unloads, selects, or deselects sections.
   */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;

    // Re-observe animations inside the newly loaded section
    if (window.Atelier._animationObserver) {
      section.querySelectorAll('[data-animate]').forEach(function (el) {
        el.classList.remove('is-visible');
        window.Atelier._animationObserver.observe(el);
      });
    }

    // Dispatch custom event for section-specific reinit
    document.dispatchEvent(
      new CustomEvent('atelier:section-loaded', {
        detail: { sectionId: event.detail.sectionId }
      })
    );
  });

  document.addEventListener('shopify:section:unload', function (event) {
    // Cleanup if needed when a section is removed in the editor
    var section = event.target;
    section.querySelectorAll('[data-animate]').forEach(function (el) {
      if (window.Atelier._animationObserver) {
        window.Atelier._animationObserver.unobserve(el);
      }
    });
  });

  document.addEventListener('shopify:section:select', function (event) {
    // Section selected in editor -- could auto-open drawers if the section
    // is a drawer, or scroll into view.
    var section = event.target;

    if (section.classList.contains('cart-drawer') && Atelier.cartDrawer) {
      Atelier.cartDrawer.open();
    }
    if (section.classList.contains('search-drawer') && Atelier.searchDrawer) {
      Atelier.searchDrawer.open();
    }
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var section = event.target;

    if (section.classList.contains('cart-drawer') && Atelier.cartDrawer) {
      Atelier.cartDrawer.close();
    }
    if (section.classList.contains('search-drawer') && Atelier.searchDrawer) {
      Atelier.searchDrawer.close();
    }
  });

  // Block-level editor events
  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    // If the block is inside an accordion, expand it
    var trigger = block.querySelector('[data-accordion-trigger]');
    if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
      trigger.click();
    }
    // If the block is a slide, navigate to it
    if (block.hasAttribute('data-slide-index')) {
      var slider = block.closest('[data-slider]');
      if (slider && slider._atelierSlider) {
        slider._atelierSlider.goTo(parseInt(block.getAttribute('data-slide-index'), 10));
      }
    }
  });
})();

/* ============================================
   13. ACCESSIBILITY
   ============================================ */

(function () {
  /**
   * Global Escape key handler: close any open drawer or modal.
   */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;

    // Close cart drawer
    var cartDrawer = document.querySelector('.cart-drawer.is-open');
    if (cartDrawer && Atelier.cartDrawer) {
      Atelier.cartDrawer.close();
      return;
    }

    // Close search drawer
    var searchDrawer = document.querySelector('.search-drawer.is-open');
    if (searchDrawer && Atelier.searchDrawer) {
      Atelier.searchDrawer.close();
      return;
    }

    // Close menu drawer
    var menuDrawer = document.querySelector('.menu-drawer.is-open');
    if (menuDrawer) {
      menuDrawer.classList.remove('is-open');
      var overlay = document.querySelector('.overlay');
      if (overlay) overlay.classList.remove('is-active');
      Atelier.unlockScroll();
      Atelier.removeTrapFocus('menu');
      document.dispatchEvent(new CustomEvent('menu:closed'));
    }
  });

  /**
   * Skip-to-content link: focus the main content area.
   */
  var skipLink = document.querySelector('[data-skip-to-content]');
  if (skipLink) {
    skipLink.addEventListener('click', function (e) {
      e.preventDefault();
      var target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus();
        target.addEventListener(
          'blur',
          function () {
            this.removeAttribute('tabindex');
          },
          { once: true }
        );
      }
    });
  }

  /**
   * Keyboard navigation support for custom slider components.
   * Left/Right arrow keys navigate slides when a slider is focused.
   */
  document.querySelectorAll('[data-slider]').forEach(function (slider) {
    slider.addEventListener('keydown', function (e) {
      if (!slider._atelierSlider) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        slider._atelierSlider.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        slider._atelierSlider.next();
      }
    });
  });

  /**
   * Detect keyboard vs mouse navigation to show/hide focus outlines.
   * Adds 'using-keyboard' class to body when Tab is pressed,
   * removes it on mouse click.
   */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') {
      document.body.classList.add('using-keyboard');
    }
  });

  document.addEventListener('mousedown', function () {
    document.body.classList.remove('using-keyboard');
  });

  /**
   * Announce dynamic content changes to screen readers.
   * Creates a live region for AJAX status messages.
   */
  var liveRegion = document.createElement('div');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.classList.add('visually-hidden');
  liveRegion.id = 'atelier-live-region';
  document.body.appendChild(liveRegion);

  /**
   * Announce a message to screen readers via the live region.
   * @param {string} message - The message to announce.
   */
  Atelier.announce = function (message) {
    var region = document.getElementById('atelier-live-region');
    if (region) {
      region.textContent = '';
      // Brief delay ensures the screen reader detects the change
      setTimeout(function () {
        region.textContent = message;
      }, 50);
    }
  };

  // Announce cart updates to screen readers
  document.addEventListener('cart:updated', function () {
    Atelier.cart.get().then(function (cart) {
      Atelier.announce('Cart updated. ' + cart.item_count + ' items in your bag.');
    });
  });
})();
